import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import {
  buildOperatingSystemLabel,
  deviceTypesLabel,
  getAudienceAttributeOptions,
  getLanguage,
  isUnitedStatesSelected,
} from '../../../helpers/study-helpers';
import { SURVEY_DETAILS, SEGMENT } from './graphql';
import ProjectCard from '../../../components/project-card';
import {
  Column,
  Container,
  FieldName,
  FieldValue,
  Grid,
  Loader,
  Row,
  Select,
  Title,
} from './styles';
import ScreenerRate from '../study/components/screener-rate';

function ScreenerVersions({ match }) {
  const { params: { segmentId } = {} } = match;

  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isPLBUX, setIsPLBUX] = useState(false);

  const {
    data: { segment } = {},
    loading: fetchingVersions,
  } = useQuery(SEGMENT, {
    variables: {
      segmentId,
    },
    onCompleted: ({ segment: { surveys, project: { participantsSource } } }) => {
      const [latestVersion] = surveys;
      const versionOptions = surveys.map(({ id, version }) => ({
        key: id,
        value: id,
        text: `Version ${version}`,
      }));

      setVersions(versionOptions);
      setSelectedVersion(latestVersion.id);
      setIsPLBUX(participantsSource === 'playbook');
    },
    skip: _.isEmpty(segmentId),
  });

  const { data: { survey } = {}, loading } = useQuery(SURVEY_DETAILS, {
    variables: {
      id: selectedVersion,
    },
    skip: !selectedVersion,
  });

  if (fetchingVersions) return <Loader data-loader="screener-loader" />;

  return (
    <Container>
      <Title>Screener survey versions</Title>
      <Select
        placeholder="Select a version"
        options={versions}
        value={selectedVersion}
        onChange={(e, { value }) => setSelectedVersion(value)}
      />

      {loading
        ? (<Loader />)
        : (
          <ProjectCard
            title={segment?.name ?? ''}
          >
            <Grid columns={2}>
              <Row>
                <Column computer={4} tablet={4} mobile={8}>
                  <FieldName>Number of Participants</FieldName>
                </Column>
                <Column computer={1} tablet={1} mobile={1}>
                  <FieldValue>{segment?.audienceParticipants}</FieldValue>
                </Column>
              </Row>

              <Row>
                <Column computer={4} tablet={4} mobile={8}>
                  <FieldName>Device</FieldName>
                </Column>
                <Column computer={12} tablet={12} mobile={8}>
                  <FieldValue>{deviceTypesLabel(segment?.device)}</FieldValue>
                </Column>
              </Row>
              {segment.device !== 'computer' && (
                <Row>
                  <Column computer={4} tablet={4} mobile={8}>
                    <FieldName>Operating System</FieldName>
                  </Column>
                  <Column computer={12} tablet={12} mobile={8}>
                    <FieldValue>{buildOperatingSystemLabel(segment?.operatingSystem ? segment.operatingSystem : [])}</FieldValue>
                  </Column>
                </Row>
              )}

              {isPLBUX && (
                <>
                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Age</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'age')}</FieldValue>
                    </Column>
                  </Row>
                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Gender</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'gender')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Ethnicity</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'ethnicity')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Household Income</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'household_income')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Country</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'location_country')}</FieldValue>
                    </Column>
                  </Row>
                  {isUnitedStatesSelected(segment.audienceAttributes) && (
                    <Row>
                      <Column computer={4} tablet={4} mobile={8}>
                        <FieldName>US States</FieldName>
                      </Column>
                      <Column computer={12} tablet={12} mobile={8}>
                        <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'location_state')}</FieldValue>
                      </Column>
                    </Row>
                  )}

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Language</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>
                        {getLanguage(segment?.participantsLanguage)}
                      </FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Job Role</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'job_role')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Current Work Status</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'work_status')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Company Size</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'company_size')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Industry</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'industry')}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Seniority</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{getAudienceAttributeOptions(segment?.audienceAttributes, 'seniority')}</FieldValue>
                    </Column>
                  </Row>

                </>
              )}

              {survey && !_.isEmpty(survey.questions) && (
                survey.questions.map((question, questionIndex) => (
                  <ScreenerRate
                    key={question.id}
                    title="Screener Question"
                    question={question}
                    questionIndex={questionIndex}
                  />
                ))
              )}
            </Grid>
          </ProjectCard>
        )}
    </Container>
  );
}

ScreenerVersions.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      segmentId: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default ScreenerVersions;
